import { addDays, addMonths, addWeeks, format, getDay, lastDayOfMonth, parseISO, startOfDay, startOfMonth } from 'date-fns';
import { Event, RecurrenceFrequency } from '../types/supabase';

type RecurrenceFields = Pick<Event, 'date' | 'time' | 'is_recurring' | 'recurrence_frequency' | 'recurrence_end_date'>;

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const ICS_DAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
const MAX_ITERATIONS = 2000;

export const FREQUENCIES: RecurrenceFrequency[] = ['weekly', 'biweekly', 'monthly_date', 'monthly_weekday'];

const ordinal = (n: number) => {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
};

// Days 29-31 always fall in a 5th week, which not every month has, so treat them as "last".
const weekdayPosition = (d: Date) => {
  const n = Math.ceil(d.getDate() / 7);
  return n >= 5 ? -1 : n;
};

const nthWeekdayOfMonth = (month: Date, weekday: number, position: number) => {
  if (position === -1) {
    let d = lastDayOfMonth(month);
    while (getDay(d) !== weekday) d = addDays(d, -1);
    return d;
  }
  let d = startOfMonth(month);
  while (getDay(d) !== weekday) d = addDays(d, 1);
  return addDays(d, (position - 1) * 7);
};

export const frequencyLabel = (frequency: RecurrenceFrequency, startDate?: string): string => {
  if (!startDate) {
    return {
      weekly: 'Every week',
      biweekly: 'Every 2 weeks',
      monthly_date: 'Monthly on the same date',
      monthly_weekday: 'Monthly on the same weekday',
    }[frequency];
  }
  const start = parseISO(startDate);
  const weekday = WEEKDAYS[getDay(start)];
  const position = weekdayPosition(start);
  return {
    weekly: `Every week on ${weekday}`,
    biweekly: `Every 2 weeks on ${weekday}`,
    monthly_date: `Monthly on the ${ordinal(start.getDate())}`,
    monthly_weekday: `Monthly on the ${position === -1 ? 'last' : ordinal(position)} ${weekday}`,
  }[frequency];
};

export const describeRecurrence = (event: RecurrenceFields): string | null => {
  if (!event.is_recurring || !event.recurrence_frequency) return null;
  const base = frequencyLabel(event.recurrence_frequency, event.date);
  return event.recurrence_end_date
    ? `${base} until ${format(parseISO(event.recurrence_end_date), 'MMM d, yyyy')}`
    : base;
};

function* iterateOccurrences(event: RecurrenceFields): Generator<Date> {
  const start = parseISO(event.date);
  if (!event.is_recurring || !event.recurrence_frequency) {
    yield start;
    return;
  }
  const end = event.recurrence_end_date ? parseISO(event.recurrence_end_date) : null;
  const weekday = getDay(start);
  const position = weekdayPosition(start);

  for (let i = 0; i < MAX_ITERATIONS; i++) {
    let d: Date;
    switch (event.recurrence_frequency) {
      case 'weekly': d = addWeeks(start, i); break;
      case 'biweekly': d = addWeeks(start, i * 2); break;
      case 'monthly_date': d = addMonths(start, i); break;
      case 'monthly_weekday': d = nthWeekdayOfMonth(addMonths(startOfMonth(start), i), weekday, position); break;
    }
    if (end && d > end) return;
    yield d;
  }
}

export const toISODate = (d: Date) => format(d, 'yyyy-MM-dd');

export const getOccurrenceDates = (event: RecurrenceFields, rangeStart: Date, rangeEnd: Date): string[] => {
  const from = startOfDay(rangeStart);
  const dates: string[] = [];
  for (const d of iterateOccurrences(event)) {
    if (d > rangeEnd) break;
    if (d >= from) dates.push(toISODate(d));
  }
  return dates;
};

export const getNextOccurrence = (event: RecurrenceFields, now = new Date()): string | null => {
  for (const d of iterateOccurrences(event)) {
    const iso = toISODate(d);
    if (parseISO(`${iso}T${event.time}`) > now) return iso;
  }
  return null;
};

export const toICSRule = (event: RecurrenceFields): string | null => {
  if (!event.is_recurring || !event.recurrence_frequency) return null;
  const start = parseISO(event.date);
  const day = ICS_DAYS[getDay(start)];
  const rule = {
    weekly: `FREQ=WEEKLY;BYDAY=${day}`,
    biweekly: `FREQ=WEEKLY;INTERVAL=2;BYDAY=${day}`,
    monthly_date: `FREQ=MONTHLY;BYMONTHDAY=${start.getDate()}`,
    monthly_weekday: `FREQ=MONTHLY;BYDAY=${weekdayPosition(start)}${day}`,
  }[event.recurrence_frequency];
  const until = event.recurrence_end_date
    ? `;UNTIL=${event.recurrence_end_date.replace(/-/g, '')}T235959Z`
    : '';
  return `RRULE:${rule}${until}`;
};
